import React, { Suspense, useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOutEverywhere } from '../../../utils/authSession.js';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import { useToast } from '../../../contexts/ToastContext.jsx';
import NotificationBell from '../notifications/NotificationBell.jsx';
import HubHelpDock from '../help/HubHelpDock.jsx';
import { HUB_NAV_SECTIONS } from '../../../config/hubNavItems.js';
import {
  filterHubNavSections,
  shouldRedirectHiddenNav,
} from '../../../utils/hubNavVisibility.js';

/** リダイレクト実行タイミング（ミリ秒） */
const LAUNCH_TRANSITION_DURATION = 1000;
/** 入場アニメーション完了まで（ミリ秒）。初回は overlay 後半合わせでパネル delay が伸びる */
const HUB_PAGE_ENTER_DURATION = 1800;
const HUB_LAYOUT_ENTER_DURATION = 2000;

function NavIcon({ name }) {
  switch (name) {
    case 'dashboard':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="9"/>
          <rect x="14" y="3" width="7" height="5"/>
          <rect x="14" y="12" width="7" height="9"/>
          <rect x="3" y="16" width="7" height="5"/>
        </svg>
      );
    case 'modules':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      );
    case 'licenses':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      );
    case 'billing':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      );
    case 'account':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      );
    case 'clinic':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      );
    case 'users':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      );
    case 'integrations':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
      );
    case 'contact':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      );
    case 'invitation':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
        </svg>
      );
    case 'legal':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      );
    default:
      return null;
  }
}

/**
 * 認証後のハブレイアウト
 * ダッシュボード、モジュール、設定ページ用
 */
const HubLayout = ({ user, clinic, licenses, setLicenses, onRefreshSession, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const canAccessBilling = user?.role === 'doctor' || user?.role === 'admin';
  const {
    effectiveIsBeta,
    betaFeaturesRestricted,
    betaDisabledByUser,
    setBetaDisabledByUser,
    showBetaToggle,
    isHubSettingsReady,
    userHubNavVisibility,
  } = useBeta();
  const { showToast } = useToast();
  const [isLaunching, setIsLaunching] = useState(false);
  const [layoutEnterPhase, setLayoutEnterPhase] = useState('pending');
  const [pageEnterPhase, setPageEnterPhase] = useState('pending');
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const mainRef = useRef(null);

  const visibleNavSections = useMemo(
    () => filterHubNavSections(HUB_NAV_SECTIONS, {
      visibilityMap: userHubNavVisibility,
      betaFeaturesRestricted,
      isHubSettingsReady,
    }),
    [userHubNavVisibility, betaFeaturesRestricted, isHubSettingsReady]
  );

  const handleTooltipEnter = useCallback((e, text) => {
    setTooltip({ visible: true, text, x: e.clientX + 14, y: e.clientY });
  }, []);
  const handleTooltipMove = useCallback((e) => {
    setTooltip(prev => ({ ...prev, x: e.clientX + 14, y: e.clientY }));
  }, []);
  const handleTooltipLeave = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  const onLaunchStart = useCallback((url) => {
    if (!url || url === '#' || (typeof url === 'string' && url.trim() === '')) return;
    setIsLaunching(true);
    setTimeout(() => {
      window.location.href = url;
    }, LAUNCH_TRANSITION_DURATION);
  }, []);

  const handleSignOut = async () => {
    try {
      setLogoutLoading(true);
      if (onLogout) onLogout();
      await signOutEverywhere();
      showToast('auth.logout.success');
      navigate('/login');
    } catch (err) {
      console.error('サインアウトエラー:', err);
    } finally {
      setLogoutLoading(false);
      setShowLogoutConfirm(false);
    }
  };

  const handleBetaToggle = () => {
    const nextDisabled = !betaDisabledByUser;
    setBetaDisabledByUser(nextDisabled);
    showToast('beta.toggle', {
      message: nextDisabled
        ? 'βモードをOFFにしました'
        : 'βモードをONにしました'
    });
  };

  // ルート変更時: paint 前に pending へ戻し done 残留による 1 フレーム flash を防ぐ
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    setPageEnterPhase('pending');
  }

  useLayoutEffect(() => {
    document.body.classList.add('hub-auth-bg');
    let enterTimer;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setLayoutEnterPhase('active');
        enterTimer = setTimeout(() => setLayoutEnterPhase('done'), HUB_LAYOUT_ENTER_DURATION);
      });
    });
    return () => {
      document.body.classList.remove('hub-auth-bg');
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      clearTimeout(enterTimer);
    };
  }, []);

  useLayoutEffect(() => {
    let enterTimer;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPageEnterPhase('active');
        enterTimer = setTimeout(() => setPageEnterPhase('done'), HUB_PAGE_ENTER_DURATION);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      clearTimeout(enterTimer);
    };
  }, [location.pathname]);

  // ページ遷移時にスクロール位置を最上部に戻す（スクロールコンテナは .hub-main）
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  // モバイル: ページ遷移時にメニューを閉じる
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // モバイル: メニュー開放中は背面スクロールを禁止
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  // ハブ設定で非表示のメニューへの直アクセスをフォールバックへ誘導
  useEffect(() => {
    if (!isHubSettingsReady) return;
    const result = shouldRedirectHiddenNav(location.pathname, userHubNavVisibility);
    if (result.redirect && result.to && result.to !== location.pathname) {
      navigate(result.to, { replace: true });
    }
  }, [isHubSettingsReady, location.pathname, userHubNavVisibility, navigate]);

  // 現在のパスがアクティブかどうかをチェック
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    if (item.billingGate) {
      if (betaFeaturesRestricted) {
        return (
          <span
            key={item.id}
            className="hub-sidebar-link hub-sidebar-disabled"
            onMouseEnter={(e) => handleTooltipEnter(e, 'β版期間中はご利用いただけません')}
            onMouseMove={handleTooltipMove}
            onMouseLeave={handleTooltipLeave}
          >
            <NavIcon name={item.icon} />
            {item.label}
          </span>
        );
      }
      if (!canAccessBilling) {
        return (
          <span
            key={item.id}
            className="hub-sidebar-link hub-sidebar-disabled"
            onMouseEnter={(e) => handleTooltipEnter(e, '医師または管理者のみ利用できます')}
            onMouseMove={handleTooltipMove}
            onMouseLeave={handleTooltipLeave}
          >
            <NavIcon name={item.icon} />
            {item.label}
          </span>
        );
      }
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        className={`hub-sidebar-link ${isActive(item.path) ? 'active' : ''}`}
      >
        <NavIcon name={item.icon} />
        {item.label}
      </Link>
    );
  };

  return (
    <div className={`hub-page hub-dashboard hub-layout-enter-${layoutEnterPhase}${isLaunching ? ' hub-launching' : ''}`}>
      {(layoutEnterPhase === 'pending' || layoutEnterPhase === 'active') && (
        <div className="hub-enter-dark-overlay" aria-hidden="true" />
      )}
      {isLaunching && (
        <div
          className="hub-launch-white-overlay"
          aria-hidden="true"
        />
      )}
      {/* Fixed Background */}
      <div
        className="hub-dashboard-background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      >
        <ShaderGradientCanvas
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          <Suspense fallback={null}>
            <ShaderGradient
              animate="on"
              axesHelper="off"
              bgColor1="#000000"
              bgColor2="#000000"
              brightness={1.2}
              cAzimuthAngle={180}
              cDistance={2.5}
              cPolarAngle={120}
              cameraZoom={1}
              color1="#ffffff"
              color2="#fafaff"
              color3="#fffafa"
              destination="onCanvas"
              embedMode="off"
              envPreset="city"
              format="gif"
              fov={45}
              frameRate={10}
              gizmoHelper="hide"
              grain="off"
              lightType="3d"
              pixelDensity={3}
              positionX={0}
              positionY={0}
              positionZ={0}
              range="disabled"
              rangeEnd={40}
              rangeStart={0}
              reflection={0.1}
              rotationX={-20}
              rotationY={0}
              rotationZ={-90}
              shader="defaults"
              type="waterPlane"
              uAmplitude={0}
              uDensity={1.7}
              uFrequency={5.5}
              uSpeed={0.1}
              uStrength={3}
              uTime={0.2}
              wireframe={false}
            />
          </Suspense>
        </ShaderGradientCanvas>
      </div>
      {/* モバイル時: サイドメニューオーバーレイ（メニュー外タップで閉じる） */}
      {mobileMenuOpen && (
        <div
          className="hub-sidebar-overlay"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* サイドバー（起動時は左にスライドアウト / モバイル時は開閉） */}
      <aside className={`hub-sidebar${isLaunching ? ' hub-launch-sidebar-out' : ''}${mobileMenuOpen ? ' open' : ''}`}>
        <div className="hub-sidebar-drawer-top">
          <button
            type="button"
            className="hub-sidebar-drawer-back"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="メニューを閉じる"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>
        <div className="hub-sidebar-header">
          <Link to="/dashboard" className="hub-sidebar-logo" onClick={() => setMobileMenuOpen(false)}>
            <img src="/logo.webp" alt="Synapse" className="hub-sidebar-logo-img" />
            <span>Synapse{effectiveIsBeta ? ' β版' : ''}</span>
          </Link>
        </div>

        <nav id="hub-sidebar-nav" className="hub-sidebar-nav">
          {!isHubSettingsReady ? (
            <>
              {[0, 1, 2].map((sectionIdx) => (
                <div key={`nav-shimmer-section-${sectionIdx}`} className="hub-sidebar-section">
                  <div
                    className="hub-shimmer"
                    style={{ width: '48px', height: '0.7rem', marginBottom: '12px', borderRadius: '4px' }}
                  />
                  {[0, 1].map((itemIdx) => (
                    <div
                      key={`nav-shimmer-${sectionIdx}-${itemIdx}`}
                      className="hub-shimmer"
                      style={{
                        width: '100%',
                        height: '40px',
                        marginBottom: '8px',
                        borderRadius: 'var(--hub-radius-md)',
                      }}
                    />
                  ))}
                </div>
              ))}
            </>
          ) : (
            visibleNavSections.map((section) => (
              <div key={section.id} className="hub-sidebar-section">
                <div className="hub-sidebar-section-title">{section.title}</div>
                {section.items.map((item) => renderNavItem(item))}
              </div>
            ))
          )}
        </nav>

        <div className="hub-sidebar-footer">
          {showBetaToggle && (
            <div 
              className="hub-beta-toggle"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                marginBottom: '8px',
                background: 'var(--hub-bg-secondary)',
                borderRadius: 'var(--hub-radius-md)',
                fontSize: '0.75rem'
              }}
            >
              <span style={{ color: 'var(--hub-text-muted)', whiteSpace: 'nowrap' }}>β切替</span>
              <button
                type="button"
                role="switch"
                aria-checked={!betaDisabledByUser}
                onClick={handleBetaToggle}
                title={betaDisabledByUser ? 'βモードをONにする' : 'βモードをOFFにする'}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--hub-radius-full)',
                  border: '1px solid var(--hub-border)',
                  background: betaDisabledByUser ? 'transparent' : 'var(--hub-accent-primary)',
                  color: betaDisabledByUser ? 'var(--hub-text-secondary)' : '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {betaDisabledByUser ? 'OFF' : 'ON'}
              </button>
            </div>
          )}
          <div className="hub-user-info">
            <div className="hub-user-avatar">
              {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </div>
            <div className="hub-user-details">
              <div className="hub-user-name">
                {user?.displayName || user?.userId || 'ユーザー'}
              </div>
              <div className="hub-user-role">
                {clinic?.name || 'クリニック'}
              </div>
            </div>
            <button 
              onClick={() => setShowLogoutConfirm(true)}
              className="hub-btn hub-btn-ghost hub-btn-sm"
              title="ログアウト"
              style={{ padding: '8px' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* カーソル追従ツールチップ */}
      <div
        ref={tooltipRef}
        className={`hub-cursor-tooltip${tooltip.visible ? ' visible' : ''}`}
        style={{ left: tooltip.x, top: tooltip.y }}
      >
        {tooltip.text}
      </div>

      {/* メインコンテンツ列（アプリバー + スクロール領域） */}
      <div className="hub-main-shell">
        <header
          className="hub-mobile-header"
          aria-label="メニュー"
        >
          {!mobileMenuOpen && (
            <button
              type="button"
              className="hub-mobile-menu-btn"
              onClick={() => setMobileMenuOpen(true)}
              aria-expanded={false}
              aria-controls="hub-sidebar-nav"
              aria-label="メニューを開く"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          <span className="hub-mobile-header-title">Synapse{effectiveIsBeta ? ' β版' : ''}</span>
        </header>

        <main
          ref={mainRef}
          className="hub-main"
        >
          <div className="hub-notification-bell-float">
            <NotificationBell user={user} clinic={clinic} />
          </div>
          <div className={`hub-page-enter-${pageEnterPhase}`}>
            {!isHubSettingsReady ? (
              <>
                <div className="hub-main-header">
                  <div className="hub-shimmer" style={{ width: '160px', height: '1.75rem', borderRadius: '6px' }} />
                </div>
                <div className="hub-main-content">
                  <div
                    className="hub-card hub-enter-panel"
                    data-panel-index="0"
                    style={{ marginBottom: 'var(--hub-space-xl)' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-lg)' }}>
                      <div>
                        <div className="hub-shimmer" style={{ width: '140px', height: '1.25rem', marginBottom: '10px' }} />
                        <div className="hub-shimmer" style={{ width: '90%', height: '0.875rem', marginBottom: '6px' }} />
                        <div className="hub-shimmer" style={{ width: '70%', height: '0.875rem' }} />
                      </div>
                      <div className="hub-shimmer" style={{ width: '100%', height: '120px', borderRadius: 'var(--hub-radius-md)' }} />
                      <div className="hub-shimmer" style={{ width: '100%', height: '72px', borderRadius: 'var(--hub-radius-md)' }} />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <Outlet context={{ user, clinic, licenses, setLicenses, isLaunching, pageEnterPhase, layoutEnterPhase, hubEnterPhase: pageEnterPhase, onLaunchStart, onRefreshSession }} />
            )}
          </div>
          <HubHelpDock />
        </main>
      </div>

      {showLogoutConfirm && (
        <div
          className="hub-modal-overlay"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 200 }}
          onClick={() => !logoutLoading && setShowLogoutConfirm(false)}
        >
          <div
            className="hub-result-modal"
            style={{
              backgroundColor: 'var(--hub-surface, #fff)',
              color: 'var(--hub-text, #1a1a1a)',
              borderRadius: 12,
              padding: 24,
              maxWidth: 420,
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              border: '1px solid var(--hub-border, #e0e0e0)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 12px', fontSize: '1.125rem', fontWeight: 600 }}>ログアウト</h2>
            <p style={{ margin: '0 0 24px', color: 'var(--hub-text-secondary)' }}>
              ログアウトしてもよろしいですか？
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                className="hub-btn hub-btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowLogoutConfirm(false)}
                disabled={logoutLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="hub-btn hub-btn-primary"
                style={{ flex: 1 }}
                onClick={handleSignOut}
                disabled={logoutLoading}
              >
                {logoutLoading ? 'ログアウト中...' : 'ログアウト'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubLayout;

